import React, { useLayoutEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { createPortal } from 'react-dom'
import { KIOT_EXPAND_FRAME_PX, kiotExpandFrameCornerRadius } from './kiotListExpandTokens'

type KiotExpandViewportBorderPortalProps = {
  scrollContainer: HTMLDivElement | null
  anchorRowEl: HTMLTableRowElement | null
  contentEl: HTMLElement | null
  widthPx?: number
  active: boolean
}

/** Viền viewport nét liền — portal trong scroll container (tr/td không stack giữa 2 hàng). */
export function KiotExpandViewportBorderPortal({
  scrollContainer,
  anchorRowEl,
  contentEl,
  widthPx,
  active,
}: KiotExpandViewportBorderPortalProps) {
  const theme = useTheme()
  const frameRef = useRef<HTMLDivElement | null>(null)
  const rowDividerRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const frame = frameRef.current
    const rowDivider = rowDividerRef.current
    if (!active || !scrollContainer || !anchorRowEl || !contentEl || !frame || !rowDivider) return

    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const scrollRect = scrollContainer.getBoundingClientRect()
        const rowRect = anchorRowEl.getBoundingClientRect()
        const contentRect = contentEl.getBoundingClientRect()
        const top = rowRect.top - scrollRect.top + scrollContainer.scrollTop
        const left = scrollContainer.scrollLeft
        const width = widthPx != null && widthPx > 0 ? widthPx : scrollContainer.clientWidth
        const height = Math.max(0, contentRect.bottom - rowRect.top)
        const rowBottom = rowRect.bottom - scrollRect.top + scrollContainer.scrollTop
        const visible = height > 0

        frame.style.top = `${top}px`
        frame.style.left = `${left}px`
        frame.style.width = `${width}px`
        frame.style.height = `${height}px`
        frame.style.display = visible ? 'block' : 'none'

        rowDivider.style.top = `${rowBottom - KIOT_EXPAND_FRAME_PX}px`
        rowDivider.style.left = `${left}px`
        rowDivider.style.width = `${width}px`
        rowDivider.style.height = `${KIOT_EXPAND_FRAME_PX}px`
        rowDivider.style.display = visible ? 'block' : 'none'
      })
    }

    measure()
    scrollContainer.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(scrollContainer)
    ro?.observe(anchorRowEl)
    ro?.observe(contentEl)

    return () => {
      cancelAnimationFrame(raf)
      scrollContainer.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [active, scrollContainer, anchorRowEl, contentEl, widthPx])

  if (!active || !scrollContainer) return null

  const c = theme.palette.primary.main
  const r = kiotExpandFrameCornerRadius(theme)
  const px = KIOT_EXPAND_FRAME_PX

  return createPortal(
    <>
      <Box
        ref={frameRef}
        aria-hidden
        data-kiot-expand-row-frame
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          boxSizing: 'border-box',
          border: `${px}px solid ${c}`,
          borderRadius: r,
          pointerEvents: 'none',
          zIndex: 15,
          backgroundColor: 'transparent',
          display: 'none',
        }}
      />
      <Box
        ref={rowDividerRef}
        aria-hidden
        data-kiot-expand-row-divider
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          boxSizing: 'border-box',
          bgcolor: c,
          pointerEvents: 'none',
          zIndex: 16,
          display: 'none',
        }}
      />
    </>,
    scrollContainer,
  )
}
